import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../services/api';
import { 
  Users, Check, X, Clock, AlertCircle, Ban, 
  ChevronRight, RefreshCw, Building2
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from './ui/dialog';

const STATUS_CONFIG = {
  pending: { color: 'bg-gold-600/20 text-gold-400', label: 'Pending Review' },
  approved: { color: 'bg-green-600/20 text-green-400', label: 'Approved' },
  rejected: { color: 'bg-red-600/20 text-red-400', label: 'Rejected' },
  suspended: { color: 'bg-gray-600/20 text-gray-400', label: 'Suspended' }
};

const AgentsPanel = () => {
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [actionDialog, setActionDialog] = useState({ open: false, action: null, agent: null });
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  const fetchAgents = async () => {
    try {
      const [allRes, pendingRes] = await Promise.all([
        api.get('/admin/agents'),
        api.get('/admin/agents/pending')
      ]);
      
      setAgents(allRes.data.agents || []);
      setPendingCount(pendingRes.data.pending_count || 0);
    } catch (error) {
      console.error('Failed to fetch agents:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgents();
  }, []);

  const handleApprove = async () => {
    if (!actionDialog.agent) return;
    
    setProcessing(true);
    try {
      await api.post(`/admin/agents/${actionDialog.agent.id}/approve`);
      toast.success('Agent approved successfully');
      setActionDialog({ open: false, action: null, agent: null });
      fetchAgents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve agent');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!actionDialog.agent || !rejectionReason) {
      toast.error('Please provide a rejection reason');
      return;
    }
    
    setProcessing(true);
    try {
      await api.post(`/admin/agents/${actionDialog.agent.id}/reject?reason=${encodeURIComponent(rejectionReason)}`);
      toast.success('Agent application rejected');
      setActionDialog({ open: false, action: null, agent: null });
      setRejectionReason('');
      fetchAgents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject agent');
    } finally {
      setProcessing(false);
    }
  };

  const handleSuspend = async () => {
    if (!actionDialog.agent || !rejectionReason) {
      toast.error('Please provide a suspension reason');
      return;
    }
    
    setProcessing(true);
    try {
      await api.post(`/admin/agents/${actionDialog.agent.id}/suspend?reason=${encodeURIComponent(rejectionReason)}`);
      toast.success('Agent suspended');
      setActionDialog({ open: false, action: null, agent: null });
      setRejectionReason('');
      fetchAgents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to suspend agent');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-gold-400" />
      </div>
    );
  }

  const pendingAgents = agents.filter(a => a.status === 'pending');
  const approvedAgents = agents.filter(a => a.status === 'approved');
  const otherAgents = agents.filter(a => !['pending', 'approved'].includes(a.status));

  return (
    <div className="space-y-6">
      {/* Pending Applications Banner */}
      {pendingCount > 0 && (
        <div className="p-4 rounded-lg bg-gold-950/20 border border-gold-600/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-gold-400" />
              <div>
                <p className="font-medium text-gold-400">{pendingCount} Pending Application{pendingCount > 1 ? 's' : ''}</p>
                <p className="text-sm text-muted-foreground">Agent applications awaiting review</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pending Applications */}
      {pendingAgents.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gold-400 mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Pending Applications
          </h3>
          <div className="space-y-3">
            {pendingAgents.map((agent) => (
              <Card key={agent.id} className="border-gold-600/30 bg-gold-950/10">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium">{agent.full_name || 'Unknown'}</h4>
                        <Badge className="bg-gold-600/20 text-gold-400">Pending</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">{agent.email}</p>
                      
                      <div className="grid grid-cols-3 gap-4 mt-3 text-sm">
                        <div>
                          <p className="text-muted-foreground">Agency</p>
                          <p className="font-medium">{agent.agency_name}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">License</p>
                          <p className="font-medium">{agent.license_number}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Experience</p>
                          <p className="font-medium">{agent.years_experience} years</p>
                        </div>
                      </div>
                      
                      {agent.specializations?.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {agent.specializations.map((spec) => (
                            <Badge key={spec} variant="outline" className="text-xs">
                              {spec}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2 ml-4">
                      <Button
                        size="sm"
                        className="btn-gold-metallic"
                        onClick={() => setActionDialog({ open: true, action: 'approve', agent })}
                      >
                        <Check className="w-3 h-3 mr-1" />
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-600/50 text-red-400 hover:bg-red-950/30"
                        onClick={() => setActionDialog({ open: true, action: 'reject', agent })}
                      >
                        <X className="w-3 h-3 mr-1" />
                        Reject
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Approved Agents */}
      {approvedAgents.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-green-400 mb-3 flex items-center gap-2">
            <Check className="w-4 h-4" />
            Active Agents ({approvedAgents.length})
          </h3>
          <div className="space-y-2">
            {approvedAgents.map((agent) => (
              <div
                key={agent.id}
                className="p-4 rounded-lg border border-white/10 hover:border-green-600/30 bg-white/5 transition-all"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-600/20 flex items-center justify-center">
                      <Users className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <p className="font-medium">{agent.full_name || agent.agency_name}</p>
                      <p className="text-sm text-muted-foreground">{agent.email}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="text-right text-sm">
                      <p className="text-muted-foreground">Properties</p>
                      <p className="font-medium">{agent.performance_metrics?.listings_created || 0}</p>
                    </div>
                    <Badge className="bg-green-600/20 text-green-400">Active</Badge>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-600/50 text-red-400 hover:bg-red-950/30"
                      onClick={() => setActionDialog({ open: true, action: 'suspend', agent })}
                    >
                      <Ban className="w-3 h-3 mr-1" />
                      Suspend
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Other Agents (Rejected/Suspended) */}
      {otherAgents.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-muted-foreground mb-3">
            Other ({otherAgents.length})
          </h3>
          <div className="space-y-2">
            {otherAgents.map((agent) => {
              const config = STATUS_CONFIG[agent.status] || STATUS_CONFIG.pending;
              return (
                <div
                  key={agent.id}
                  className="p-4 rounded-lg border border-white/10 bg-white/5"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{agent.full_name || agent.agency_name}</p>
                      <p className="text-sm text-muted-foreground">{agent.email}</p>
                    </div>
                    <Badge className={config.color}>{config.label}</Badge>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty State */}
      {agents.length === 0 && (
        <div className="text-center py-12">
          <Users className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
          <h3 className="text-lg font-medium">No Agent Applications</h3>
          <p className="text-sm text-muted-foreground mt-2">
            Agent applications will appear here for review.
          </p>
        </div>
      )}

      {/* Action Dialog */}
      <Dialog open={actionDialog.open} onOpenChange={(open) => !open && setActionDialog({ open: false, action: null, agent: null })}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>
              {actionDialog.action === 'approve' && 'Approve Agent Application'}
              {actionDialog.action === 'reject' && 'Reject Agent Application'}
              {actionDialog.action === 'suspend' && 'Suspend Agent'}
            </DialogTitle>
            <DialogDescription>
              {actionDialog.action === 'approve' && `Approve ${actionDialog.agent?.full_name || actionDialog.agent?.agency_name} as a verified agent?`}
              {actionDialog.action === 'reject' && `Reject the application from ${actionDialog.agent?.full_name || actionDialog.agent?.agency_name}?`}
              {actionDialog.action === 'suspend' && `Suspend agent ${actionDialog.agent?.full_name || actionDialog.agent?.agency_name}? They will lose access to the Agent Portal.`}
            </DialogDescription>
          </DialogHeader>

          {(actionDialog.action === 'reject' || actionDialog.action === 'suspend') && (
            <div className="py-4">
              <label className="text-sm font-medium">Reason *</label>
              <Input
                placeholder={`Reason for ${actionDialog.action}...`}
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="mt-2 bg-white/5 border-white/10"
              />
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog({ open: false, action: null, agent: null })}>
              Cancel
            </Button>
            <Button
              className={actionDialog.action === 'approve' ? 'btn-gold-metallic' : 'bg-red-600 hover:bg-red-700'}
              onClick={() => {
                if (actionDialog.action === 'approve') handleApprove();
                if (actionDialog.action === 'reject') handleReject();
                if (actionDialog.action === 'suspend') handleSuspend();
              }}
              disabled={processing || ((actionDialog.action === 'reject' || actionDialog.action === 'suspend') && !rejectionReason)}
            >
              {processing ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
              ) : (
                actionDialog.action === 'approve' ? 'Approve Agent' :
                actionDialog.action === 'reject' ? 'Reject Application' :
                'Suspend Agent'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AgentsPanel;
